function coercePhoneUser(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const r = raw;
    const id = typeof r.id === 'number' ? r.id : Number(r.id);
    if (!Number.isFinite(id))
        return null;
    const branchRaw = r.branch;
    let branch = null;
    if (branchRaw && typeof branchRaw === 'object') {
        const b = branchRaw;
        branch = {
            id: typeof b.id === 'number' ? b.id : Number(b.id) || 0,
            branchName: String(b.branchName ?? ''),
        };
    }
    return {
        id,
        username: String(r.username ?? ''),
        name: String(r.name ?? ''),
        phoneUser: String(r.phoneUser ?? ''),
        isPhonePause: Boolean(r.isPhonePause),
        status: String(r.status ?? ''),
        idleTime: String(r.idleTime ?? ''),
        userType: String(r.userType ?? ''),
        email: String(r.email ?? ''),
        phoneIp: String(r.phoneIp ?? ''),
        phonePassword: String(r.phonePassword ?? ''),
        phonePort: String(r.phonePort ?? ''),
        contactNumber: String(r.contactNumber ?? ''),
        position: String(r.position ?? ''),
        shift: String(r.shift ?? ''),
        userId: typeof r.userId === 'number' ? r.userId : Number(r.userId) || 0,
        branch,
    };
}
function coercePhoneUserList(raw) {
    if (!Array.isArray(raw))
        return [];
    return raw.map(coercePhoneUser).filter((u) => u !== null);
}
/** Status label when CRM/PBX reports the agent on break (queue paused). */
export const IN_BREAK_DISPLAY_STATUS = 'In break';
function isPauseLikeStatus(status) {
    const normalized = status.trim().toLowerCase();
    return (normalized === 'pause' ||
        normalized === 'paused' ||
        normalized === 'on pause' ||
        normalized === 'on break' ||
        normalized.includes('paused') ||
        normalized.includes('on break'));
}
/** Label shown in the Status column (`isPhonePause` → ACW; pause-like `status` → in break). */
export function phoneUserDisplayStatus(user) {
    if (user.isPhonePause)
        return 'ACW';
    const trimmed = user.status.trim();
    if (!trimmed)
        return '—';
    if (isPauseLikeStatus(trimmed))
        return IN_BREAK_DISPLAY_STATUS;
    return trimmed;
}
const ACW_DISPLAY_STATUS = 'ACW';
function displayStatusToQstatus(display) {
    return display
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '_');
}
/** Build Spring `PhoneUserCriteria` query params for `GET /api/qstat/phone-users`. */
export function buildPhoneUsersQueryParams(query) {
    const q = new URLSearchParams();
    if (query.branchIds?.length) {
        q.set('branchId.in', query.branchIds.map(String).join(','));
    }
    if (query.agentNames?.length) {
        q.set('name.in', query.agentNames.join(','));
    }
    const statuses = (query.displayStatuses ?? []).map((s) => s.trim()).filter(Boolean);
    const wantsAcw = statuses.some((s) => s.toUpperCase() === ACW_DISPLAY_STATUS);
    const wantsInBreak = statuses.some((s) => s.toLowerCase() === IN_BREAK_DISPLAY_STATUS.toLowerCase());
    const qstatusValues = statuses
        .filter((s) => {
        const upper = s.toUpperCase();
        return upper !== ACW_DISPLAY_STATUS && s.toLowerCase() !== IN_BREAK_DISPLAY_STATUS.toLowerCase();
    })
        .map(displayStatusToQstatus);
    if (wantsAcw) {
        q.set('isPhonePause.equals', 'true');
    }
    if (wantsInBreak) {
        qstatusValues.push('PAUSED');
    }
    if (qstatusValues.length > 0) {
        q.set('qstatus.in', qstatusValues.join(','));
    }
    return q;
}
export function extractPhoneUsersFilterReference(users) {
    const branchById = new Map();
    const agentNames = new Set();
    const displayStatuses = new Set();
    for (const user of users) {
        if (user.branch?.id && user.branch.branchName.trim()) {
            branchById.set(user.branch.id, {
                id: user.branch.id,
                branchName: user.branch.branchName.trim(),
            });
        }
        if (user.name.trim())
            agentNames.add(user.name.trim());
        const label = phoneUserDisplayStatus(user);
        if (label !== '—')
            displayStatuses.add(label);
    }
    return {
        branches: [...branchById.values()].sort((a, b) => a.branchName.localeCompare(b.branchName)),
        agentNames: [...agentNames].sort((a, b) => a.localeCompare(b)),
        displayStatuses: [...displayStatuses].sort((a, b) => a.localeCompare(b)),
    };
}
/**
 * Coerce CRM `GET /api/qstat/phone-users` payload.
 * Accepts `{ asteriskConnected, users: [...] }` or a bare user array.
 */
export function normalizePhoneUsersResponse(raw) {
    if (Array.isArray(raw)) {
        return { asteriskConnected: true, users: coercePhoneUserList(raw) };
    }
    if (raw && typeof raw === 'object') {
        const r = raw;
        if (Array.isArray(r.users)) {
            return {
                asteriskConnected: Boolean(r.asteriskConnected),
                users: coercePhoneUserList(r.users),
            };
        }
        const single = coercePhoneUser(raw);
        return { asteriskConnected: true, users: single ? [single] : [] };
    }
    return { asteriskConnected: false, users: [] };
}
/** @deprecated Use {@link normalizePhoneUsersResponse} — list only, no `asteriskConnected`. */
export function normalizePhoneUsers(raw) {
    return normalizePhoneUsersResponse(raw).users;
}
